import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { upsertBdData, fetchSkyleadAllAccounts } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings, Save, Plus, X, Users, Link as LinkIcon, Linkedin } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PERFIL = () => ({
  nombre: "",
  rol: "Comercial",
  red: "LinkedIn",
  prospectos_objetivo: 0,
  invitaciones_objetivo: 0,
  mensajes_objetivo: 0,
  mercados: [],
});

const COMMITMENT_FIELDS = [
  { key: "correos_descubiertos", label: "Correos descubiertos" },
  { key: "bases_armadas", label: "Bases armadas" },
  { key: "contactos_prospectados", label: "Contactos prospectados" },
  { key: "contactos_contactados", label: "Contactos contactados" },
  { key: "inbound_gestionado", label: "Inbound gestionado" },
  { key: "reuniones_estimadas", label: "Reuniones estimadas" },
];

export default function BdSettingsDialog({ clientId, open, onOpenChange, current }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    sheet_url: "",
    sheet_id: "",
    briefs_doc_url: "",
    gids: { briefs: "2140655749", leads: "536845030" },
    perfiles: [],
    commitments: {},
    period_start: "",
    period_end: "",
    kick_off: "",
    inicio_outbound: "",
    finalizacion: "",
    meses_adicionales: 0,
    objetivo_reuniones: 0,
    notes: "",
    skylead_accounts: [],
    por_fuera_sheet_url: "",
  });
  const [mercadoInputs, setMercadoInputs] = useState({});
  const [seatSearch, setSeatSearch] = useState("");
  const seatsQ = useQuery({ queryKey: ["sk-all-accounts"], queryFn: fetchSkyleadAllAccounts, enabled: open, staleTime: 5 * 60_000 });

  useEffect(() => {
    if (open && current) {
      const cfg = current.config || {};
      setForm({
        sheet_url: cfg.sheet_url || "",
        sheet_id: cfg.sheet_id || "",
        briefs_doc_url: cfg.briefs_doc_url || "",
        gids: cfg.gids || { briefs: "2140655749", leads: "536845030" },
        perfiles: cfg.perfiles || [],
        commitments: cfg.commitments || {},
        period_start: cfg.period_start || "",
        period_end: cfg.period_end || "",
        kick_off: cfg.kick_off || "",
        inicio_outbound: cfg.inicio_outbound || "",
        finalizacion: cfg.finalizacion || "",
        meses_adicionales: cfg.meses_adicionales || 0,
        objetivo_reuniones: cfg.objetivo_reuniones || 0,
        notes: cfg.notes || "",
        skylead_accounts: cfg.skylead_accounts || [],
        por_fuera_sheet_url: cfg.por_fuera_sheet_url || "",
      });
      setMercadoInputs({});
    }
  }, [open, current]);

  const save = useMutation({
    mutationFn: () => upsertBdData(clientId, { config: form }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bd-data", clientId] });
      qc.invalidateQueries({ queryKey: ["bd-summary", clientId] });
      qc.invalidateQueries({ queryKey: ["bd-briefs", clientId] });
      qc.invalidateQueries({ queryKey: ["bd-leads", clientId] });
      toast.success("Compromiso BD guardado");
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });

  const updatePerfil = (i, patch) => {
    const arr = [...form.perfiles]; arr[i] = { ...arr[i], ...patch }; setForm({ ...form, perfiles: arr });
  };
  const removePerfil = (i) => {
    const arr = [...form.perfiles]; arr.splice(i, 1); setForm({ ...form, perfiles: arr });
  };
  const addPerfil = () => setForm({ ...form, perfiles: [...form.perfiles, DEFAULT_PERFIL()] });

  const addMercadoToPerfil = (i) => {
    const v = (mercadoInputs[i] || "").trim();
    if (!v) return;
    const cur = form.perfiles[i].mercados || [];
    if (cur.includes(v)) { setMercadoInputs({ ...mercadoInputs, [i]: "" }); return; }
    updatePerfil(i, { mercados: [...cur, v] });
    setMercadoInputs({ ...mercadoInputs, [i]: "" });
  };
  const removeMercadoFromPerfil = (i, m) => {
    updatePerfil(i, { mercados: (form.perfiles[i].mercados || []).filter((x) => x !== m) });
  };

  const updateCommitment = (key, value) => setForm({ ...form, commitments: { ...form.commitments, [key]: Number(value) } });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Settings className="h-5 w-5 text-indigo-600" />Compromiso Desarrollo de Negocios</DialogTitle>
          <DialogDescription>Definí período, perfiles activos, mercados objetivo, compromisos mensuales y la fuente Google Sheet de la operación.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2" data-testid="bd-settings-form">
          {/* Fuente Google Sheet */}
          <Section title="Fuente de datos · Google Sheet" highlight>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">URL del Sheet (compartido como Cualquiera con el enlace)</Label>
            <div className="flex gap-2 mt-1">
              <div className="relative flex-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input value={form.sheet_url} onChange={(e) => setForm({ ...form, sheet_url: e.target.value })} placeholder="https://docs.google.com/spreadsheets/d/…" className="pl-9 text-xs" data-testid="bd-sheet-url" />
              </div>
            </div>
            {form.sheet_id && <p className="text-[10px] text-slate-500 mt-1 font-mono">sheet_id detectado: {form.sheet_id}</p>}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">GID · Pestaña BRIEFS (Reuniones)</Label>
                <Input value={form.gids.briefs} onChange={(e) => setForm({ ...form, gids: { ...form.gids, briefs: e.target.value } })} placeholder="2140655749" className="text-xs font-mono" data-testid="bd-gid-briefs" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">GID · Pestaña LEADS (Inbound)</Label>
                <Input value={form.gids.leads} onChange={(e) => setForm({ ...form, gids: { ...form.gids, leads: e.target.value } })} placeholder="536845030" className="text-xs font-mono" data-testid="bd-gid-leads" />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">URL del Google Doc con los Briefs detallados</Label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input value={form.briefs_doc_url} onChange={(e) => setForm({ ...form, briefs_doc_url: e.target.value })} placeholder="https://docs.google.com/document/d/…" className="pl-9 text-xs" data-testid="bd-briefs-doc-url" />
              </div>
              <p className="text-[10px] text-slate-500 mt-1 italic">El Doc debe estar como &quot;Cualquiera con el enlace · Lector&quot;. Pulse parsea por nombre de empresa.</p>
            </div>
          </Section>

          {/* Período */}
          <Section title="Período del compromiso">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Desde</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="bd-period-start" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hasta</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="bd-period-end" /></div>
            </div>
          </Section>

          {/* Plan del proyecto (Reuniones) */}
          <Section title="Plan del proyecto · Reuniones" highlight>
            <p className="text-[11px] text-slate-500 -mt-1 mb-2">Fechas y objetivo total. Pulse calcula automáticamente días hábiles, reus esperadas y el semáforo.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fecha Kick Off</Label><Input type="date" value={form.kick_off} onChange={(e) => setForm({ ...form, kick_off: e.target.value })} data-testid="bd-kick-off" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fecha Inicio Outbound</Label><Input type="date" value={form.inicio_outbound} onChange={(e) => setForm({ ...form, inicio_outbound: e.target.value })} data-testid="bd-inicio-outbound" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fecha Finalización</Label><Input type="date" value={form.finalizacion} onChange={(e) => setForm({ ...form, finalizacion: e.target.value })} data-testid="bd-finalizacion" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Meses adicionales</Label><Input type="number" min="0" value={form.meses_adicionales} onChange={(e) => setForm({ ...form, meses_adicionales: Number(e.target.value) })} className="text-right font-mono" data-testid="bd-meses-adic" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Objetivo de reuniones</Label><Input type="number" min="0" value={form.objetivo_reuniones} onChange={(e) => setForm({ ...form, objetivo_reuniones: Number(e.target.value) })} className="text-right font-mono font-bold" data-testid="bd-objetivo-reuniones" /></div>
            </div>
          </Section>

          {/* Perfiles activos */}
          <Section title="Perfiles activos" highlight>
            <p className="text-[11px] text-slate-500 -mt-1 mb-2">Cada perfil (LinkedIn corporativo, comercial, founder) con su objetivo de prospección y los mercados que trabaja.</p>
            <div className="space-y-3 mb-2">
              {form.perfiles.map((p, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid={`bd-perfil-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <Input placeholder="Nombre" value={p.nombre} onChange={(e) => updatePerfil(i, { nombre: e.target.value })} className="h-8 col-span-3 text-xs" />
                    <Input placeholder="Rol" value={p.rol} onChange={(e) => updatePerfil(i, { rol: e.target.value })} className="h-8 col-span-2 text-xs" />
                    <select value={p.red} onChange={(e) => updatePerfil(i, { red: e.target.value })} className="h-8 col-span-2 text-xs border border-slate-200 rounded-md px-2 bg-white">
                      <option>LinkedIn</option><option>Email</option><option>LinkedIn + Email</option>
                    </select>
                    <Input type="number" min="0" placeholder="Prosp." title="Prospectos en el período" value={p.prospectos_objetivo || 0} onChange={(e) => updatePerfil(i, { prospectos_objetivo: Number(e.target.value) })} className="h-8 col-span-1 text-xs text-right font-mono" />
                    <Input type="number" min="0" placeholder="Inv." title="Invitaciones en el período" value={p.invitaciones_objetivo || 0} onChange={(e) => updatePerfil(i, { invitaciones_objetivo: Number(e.target.value) })} className="h-8 col-span-1 text-xs text-right font-mono" />
                    <Input type="number" min="0" placeholder="Msj." title="Mensajes en el período" value={p.mensajes_objetivo || 0} onChange={(e) => updatePerfil(i, { mensajes_objetivo: Number(e.target.value) })} className="h-8 col-span-1 text-xs text-right font-mono" />
                    <div className="col-span-1" />
                    <button type="button" onClick={() => removePerfil(i)} className="text-rose-500 hover:text-rose-700 col-span-1 flex justify-end" data-testid={`bd-rm-perfil-${i}`}><X className="h-4 w-4" /></button>
                  </div>
                  {/* Mercados del perfil */}
                  <div className="pl-1 pt-1 border-t border-slate-100">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Mercados del perfil</div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {(p.mercados || []).map((m) => (
                        <span key={m} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded-sm font-medium">
                          {m}
                          <button type="button" onClick={() => removeMercadoFromPerfil(i, m)} className="text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                      <div className="flex items-center gap-1">
                        <Input
                          value={mercadoInputs[i] || ""}
                          onChange={(e) => setMercadoInputs({ ...mercadoInputs, [i]: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMercadoToPerfil(i))}
                          placeholder="Ej: Argentina, México…"
                          className="h-7 text-[11px] w-44"
                          data-testid={`bd-perfil-${i}-mercado-input`}
                        />
                        <button type="button" onClick={() => addMercadoToPerfil(i)} className="text-[11px] text-indigo-600 hover:text-indigo-800 px-2 py-1 border border-slate-200 rounded-sm" data-testid={`bd-perfil-${i}-add-mercado`}>+</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {form.perfiles.length === 0 && <div className="text-xs text-slate-400 italic">Sin perfiles cargados.</div>}
            </div>
            <div className="text-[10px] text-slate-500 mb-2 grid grid-cols-12 gap-2 px-3">
              <div className="col-span-3">Nombre</div><div className="col-span-2">Rol</div><div className="col-span-2">Red</div><div className="col-span-1 text-right">Prosp.</div><div className="col-span-1 text-right">Inv.</div><div className="col-span-1 text-right">Msj.</div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addPerfil} data-testid="bd-add-perfil"><Plus className="h-3.5 w-3.5 mr-1" /> Agregar perfil</Button>
          </Section>

          {/* Perfiles outreach (LinkedIn seats) */}
          <Section title="Perfiles outreach" highlight>
            <p className="text-[11px] text-slate-500 -mt-1 mb-2">Asociá uno o más perfiles LinkedIn de la plataforma de outreach. Las métricas del tab <span className="font-semibold">Outbound</span> traen invitaciones, aceptaciones, mensajes y respuestas en vivo.</p>
            <div className="space-y-2 mb-2">
              {(form.skylead_accounts || []).map((sa, i) => (
                <div key={`${sa.account_id}-${i}`} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-md px-3 py-2" data-testid={`bd-skylead-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Linkedin className="h-3.5 w-3.5 text-[#0A66C2] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-slate-900 truncate">{sa.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">user#{sa.user_id} · acc#{sa.account_id}</div>
                    </div>
                  </div>
                  <button type="button" onClick={() => setForm({ ...form, skylead_accounts: form.skylead_accounts.filter((_, idx) => idx !== i) })} className="text-rose-500 hover:text-rose-700"><X className="h-4 w-4" /></button>
                </div>
              ))}
              {(form.skylead_accounts || []).length === 0 && (
                <div className="text-xs text-slate-400 italic">Sin perfiles asociados.</div>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Agregar perfil</Label>
              <Input
                value={seatSearch}
                onChange={(e) => setSeatSearch(e.target.value)}
                placeholder={seatsQ.isLoading ? "Cargando perfiles…" : "Buscar por nombre…"}
                className="h-9 text-xs mt-1"
                data-testid="bd-skylead-search"
              />
              {seatsQ.data && seatSearch && (
                <div className="mt-2 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                  {(seatsQ.data.accounts || [])
                    .filter((a) => a.name.toLowerCase().includes(seatSearch.toLowerCase()))
                    .filter((a) => !(form.skylead_accounts || []).some((sa) => sa.account_id === a.account_id))
                    .slice(0, 15)
                    .map((a) => (
                      <button
                        type="button"
                        key={a.account_id}
                        onClick={() => {
                          setForm({ ...form, skylead_accounts: [...(form.skylead_accounts || []), { user_id: a.user_id, account_id: a.account_id, name: a.name }] });
                          setSeatSearch("");
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                        data-testid={`bd-skylead-pick-${a.account_id}`}
                      >
                        <Linkedin className="h-3.5 w-3.5 text-[#0A66C2] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-slate-900 truncate">{a.name}</div>
                          <div className="text-[10px] text-slate-500 font-mono">team#{a.team_id} · acc#{a.account_id}</div>
                        </div>
                        <Plus className="h-3.5 w-3.5 text-indigo-600" />
                      </button>
                    ))}
                  {(seatsQ.data.accounts || []).filter((a) => a.name.toLowerCase().includes(seatSearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400 italic">Sin resultados.</div>
                  )}
                </div>
              )}
              {seatsQ.data && !seatSearch && (
                <p className="text-[10px] text-slate-500 mt-1">{seatsQ.data.count} perfiles disponibles. Escribí el nombre para buscar.</p>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-slate-200">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">URL hoja Por Fuera (Google Sheets)</Label>
              <p className="text-[11px] text-slate-500 -mt-0.5 mb-1.5">Abrí la planilla, navegá a la pestaña del cliente y copiá el URL completo (que incluye <code>#gid=…</code>). El backend lo convierte automáticamente a CSV.</p>
              <Input
                value={form.por_fuera_sheet_url || ""}
                onChange={(e) => setForm({ ...form, por_fuera_sheet_url: e.target.value })}
                placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=…"
                className="h-9 text-xs"
                data-testid="bd-porfuera-url"
              />
            </div>
          </Section>

          {/* Compromisos numéricos (período total) */}
          <Section title="Compromisos del período (global)">
            <p className="text-[11px] text-slate-500 -mt-1 mb-2">Estos objetivos aplican al período completo del cliente, no por perfil individual.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {COMMITMENT_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</Label>
                  <Input type="number" min="0" value={form.commitments[f.key] || 0} onChange={(e) => updateCommitment(f.key, e.target.value)} className="h-8 text-xs text-right font-mono" data-testid={`bd-com-${f.key}`} />
                </div>
              ))}
            </div>
          </Section>

          <Section title="Notas (opcional)">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contexto, supuestos, dependencias…" className="text-xs min-h-[80px]" data-testid="bd-notes" />
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bd-save-btn">
            <Save className="h-4 w-4 mr-1.5" />{save.isPending ? "Guardando..." : "Guardar compromiso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children, highlight }) {
  return (
    <div className={`rounded-md p-4 ${highlight ? "bg-indigo-50/50 border border-indigo-100" : "bg-slate-50 border border-slate-100"}`}>
      <h4 className="font-display text-xs font-semibold text-slate-900 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        {highlight && <Users className="h-3.5 w-3.5 text-indigo-600" />}
        {title}
      </h4>
      {children}
    </div>
  );
}
